import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  Nacionalidad,
  NacionalidadesPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchNacionalidades = ({
  page = 1,
  page_size = 10,
  nombre = '',
  codigo = '',
  pais = '',
}: GetNacionalidadesParams) => {
  return useQuery({
    queryKey: ['nacionalidades', page, nombre, page_size, codigo, pais],
    queryFn: async () => {
      return getNacionalidades({ page, nombre, page_size, codigo, pais });
    },
  });
};

export const useGetNacionalidad = (id: string) => {
  return useQuery({
    queryKey: ['nacionalidades', id],
    queryFn: async () => {
      return getNacionalidad(id);
    },
  });
};

export const useCreateNacionalidad = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createNacionalidad,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nacionalidades'] });
    },
    onSettled: () => {
      navigate(returnUrl, { replace: true });
    },
  });
};

export const useUpdateNacionalidad = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNacionalidad,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nacionalidades'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetNacionalidadesParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  codigo?: string;
  pais?: string; // fk name
};

export type CreateNacionalidadParams = Omit<Nacionalidad, 'id_nacionalidad'>;
export type UpdateNacionalidadParams = {
  id: string;
  data: CreateNacionalidadParams;
};

export const getNacionalidades = async (params?: GetNacionalidadesParams) => {
  const {
    page = 1,
    page_size = 10,
    nombre = '',
    codigo = '',
    pais = '',
  } = params || {};

  return get<NacionalidadesPagingRes>(
    `${urlBackend}api/v1/nacionalidades/?page=${page}&page_size=${page_size}&nombre=${nombre}&codigo=${codigo}&pais=${pais}`,
    true
  );
};

export const getNacionalidad = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Nacionalidad>(
    `${urlBackend}api/v1/nacionalidades/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createNacionalidad = async (data: CreateNacionalidadParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Nacionalidad>(
    `${urlBackend}api/v1/nacionalidades/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateNacionalidad = async ({
  id,
  data,
}: UpdateNacionalidadParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Nacionalidad>(
    `${urlBackend}api/v1/nacionalidades/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
